import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, Marker, Popup, useMapEvents } from "react-leaflet";
import L from "leaflet";

import PhotographerCard from "./PhotographerCard";
import { readMapViewport, sameBrowseView } from "../../utils/browseState";

import "../../styles/components/browse_photographers_page/PhotographerMap.css";

const cameraSvg = `
  <svg
    class="camera-map-marker__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
`;

function createCameraMarker(active = false) {
  return L.divIcon({
    className: "camera-map-marker-leaflet",
    html: `
      <div class="camera-map-marker${active ? " camera-map-marker--active" : ""}">
        ${cameraSvg}
        <span class="camera-map-marker__flash" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </div>
    `,
    iconSize: [40, 44],
    iconAnchor: [20, 40],
    popupAnchor: [0, -38],
  });
}

const defaultCameraIcon = createCameraMarker(false);
const activeCameraIcon = createCameraMarker(true);

function MapViewportSync({ view, onViewChange, onInteraction, onMoveStart }) {
  const map = useMap();
  const applyingView = useRef(false);

  useEffect(() => {
    if (!sameBrowseView(readMapViewport(map).view, view)) {
      applyingView.current = true;
      map.stop();
      map.setView(view.center, view.zoom, { animate: false });
      applyingView.current = false;
    }
    onViewChange(readMapViewport(map));
  }, [map, view, onViewChange]);

  useMapEvents({
    movestart() { if (!applyingView.current) onMoveStart(); },
    mousedown() { onInteraction(); },
    moveend() { if (!applyingView.current) onViewChange(readMapViewport(map)); },
    resize() { if (!applyingView.current) onViewChange(readMapViewport(map)); },
  });

  return null;
}

function InvalidateSizeOnExpand({ isExpanded }) {
  const map = useMap();

  useEffect(() => {
    const timeout = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    return () => clearTimeout(timeout);
  }, [isExpanded, map]);

  return null;
}

function PhotographerMarker({ photographer, isActive }) {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const position = useMemo(
    () => [photographer.latitude, photographer.longitude],
    [photographer.latitude, photographer.longitude]
  );
  const eventHandlers = useMemo(() => ({
    popupopen: () => setIsPopupOpen(true),
    popupclose: () => setIsPopupOpen(false),
  }), []);
  // Updating the icon should not re-layout and auto-pan an open popup.
  const popupContent = useMemo(
    () => <PhotographerCard photographer={photographer} />,
    [photographer]
  );

  return (
    <Marker
      position={position}
      icon={isActive || isPopupOpen ? activeCameraIcon : defaultCameraIcon}
      eventHandlers={eventHandlers}
    >
      <Popup>{popupContent}</Popup>
    </Marker>
  );
}

export default function PhotographerMap({
  photographers,
  view,
  onViewChange,
  onInteraction,
  onMoveStart,
  hoveredSub,
  selectedSub,
  isMapExpanded,
}) {
  return (
    <MapContainer
      center={view.center}
      zoom={view.zoom}
      scrollWheelZoom={true}
      className="leaflet-map"
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; OpenStreetMap contributors"
      />

      <MapViewportSync view={view} onViewChange={onViewChange} onInteraction={onInteraction} onMoveStart={onMoveStart} />

      <InvalidateSizeOnExpand isExpanded={isMapExpanded} />

      {photographers.map((p) => {
        if (p.latitude && p.longitude) {
          return (
            <PhotographerMarker
              key={p.sub}
              photographer={p}
              isActive={p.sub === hoveredSub || p.sub === selectedSub}
            />
          );
        }

        return null;
      })}
    </MapContainer>
  );
}
